/**
 * Volume profile computed client-side from the OHLCV bars the chart already
 * has. Mirrors `core/volume_profile.py` so the chart overlay and any
 * strategy that hits the backend get the same numbers.
 *
 * Each bar's volume is *distributed* across all price buckets that overlap
 * its [low, high] range, weighted by the overlap length. This produces a
 * proper TradingView-style profile where a single bar contributes to
 * multiple horizontal rows (rather than dumping all its volume into the
 * single bucket containing its typical price). Value area expands from
 * POC outward until ~70% of total volume is included.
 */

export interface VPBar {
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface VPLevel {
  lo: number;
  hi: number;
  volume: number;
  mid: number;
}

export interface VolumeProfileResult {
  poc: number;
  vah: number;
  val: number;
  levels: VPLevel[];
  totalVolume: number;
}

export function computeVolumeProfile(
  bars: VPBar[],
  numLevels = 30,
  valueAreaPct = 0.7,
): VolumeProfileResult | null {
  if (bars.length === 0) return null;
  if (numLevels < 2) return null;
  if (valueAreaPct <= 0 || valueAreaPct > 1) return null;

  let priceLo = Infinity;
  let priceHi = -Infinity;
  for (const b of bars) {
    if (b.low < priceLo) priceLo = b.low;
    if (b.high > priceHi) priceHi = b.high;
  }
  if (!Number.isFinite(priceLo) || !Number.isFinite(priceHi)) return null;
  if (priceHi <= priceLo) return null;

  const bucketSize = (priceHi - priceLo) / numLevels;
  if (bucketSize <= 0) return null;

  // Distribute each bar's volume across all buckets its [low, high] range
  // overlaps, weighted by the overlap length. A doji whose high == low
  // collapses to a single bucket containing that price.
  const distribute = (
    out: number[],
    low: number,
    high: number,
    weight: number,
  ): void => {
    if (weight === 0) return;
    if (high <= low) {
      let idx = Math.floor((low - priceLo) / bucketSize);
      if (idx >= numLevels) idx = numLevels - 1;
      if (idx < 0) idx = 0;
      out[idx] += weight;
      return;
    }
    const startRaw = Math.floor((low - priceLo) / bucketSize);
    const endRaw = Math.floor((high - priceLo) / bucketSize);
    const startIdx = Math.max(0, Math.min(numLevels - 1, startRaw));
    const endIdx = Math.max(0, Math.min(numLevels - 1, endRaw));
    const barRange = high - low;
    for (let i = startIdx; i <= endIdx; i++) {
      const bucketLoEdge = priceLo + i * bucketSize;
      const bucketHiEdge = priceLo + (i + 1) * bucketSize;
      const overlapLo = Math.max(bucketLoEdge, low);
      const overlapHi = Math.min(bucketHiEdge, high);
      const span = overlapHi - overlapLo;
      if (span <= 0) continue;
      out[i] += (span / barRange) * weight;
    }
  };

  const buckets: number[] = new Array(numLevels).fill(0);
  for (const b of bars) {
    distribute(buckets, b.low, b.high, b.volume);
  }

  let totalVolume = buckets.reduce((acc, v) => acc + v, 0);
  if (totalVolume === 0) {
    // Indices (e.g. NIFTY 50) don't carry volume because they're not directly
    // traded — only their derivatives are. Fall back to a *time-based* profile
    // where each bar contributes weight 1 distributed across its [low, high]
    // range. POC then means "price level the market spent the most time at"
    // instead of "highest traded volume," still useful for support /
    // resistance.
    buckets.fill(0);
    for (const b of bars) {
      distribute(buckets, b.low, b.high, 1);
    }
    totalVolume = buckets.reduce((acc, v) => acc + v, 0);
    if (totalVolume === 0) return null;
  }

  const levels: VPLevel[] = buckets.map((v, i) => {
    const lo = priceLo + bucketSize * i;
    const hi = priceLo + bucketSize * (i + 1);
    return { lo, hi, volume: v, mid: (lo + hi) / 2 };
  });

  let pocIdx = 0;
  for (let i = 1; i < numLevels; i++) {
    if (buckets[i] > buckets[pocIdx]) pocIdx = i;
  }
  const poc = levels[pocIdx].mid;

  const target = totalVolume * valueAreaPct;
  let loIdx = pocIdx;
  let hiIdx = pocIdx;
  let accumulated = buckets[pocIdx];
  while (accumulated < target && (loIdx > 0 || hiIdx < numLevels - 1)) {
    const leftVol = loIdx > 0 ? buckets[loIdx - 1] : -1;
    const rightVol = hiIdx < numLevels - 1 ? buckets[hiIdx + 1] : -1;
    if (rightVol >= leftVol) {
      hiIdx += 1;
      accumulated += buckets[hiIdx];
    } else {
      loIdx -= 1;
      accumulated += buckets[loIdx];
    }
  }

  return {
    poc,
    vah: levels[hiIdx].hi,
    val: levels[loIdx].lo,
    levels,
    totalVolume,
  };
}
